import React, { useState } from 'react';
import {
  Avatar, Box, Flex, Switch, Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LuCheck, LuLogOut, LuMonitor, LuMoon, LuSun,
} from 'react-icons/lu';
import { useAuth } from '../../contexts/AuthContext';
import { useColorPreference } from '../../contexts/ColorPreferenceContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { SUPPORTED_LANGUAGES } from '../../i18n/languages';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';
import PhoneConfirmSheet from './PhoneConfirmSheet';
import { ListCard, SectionCaption } from './PhoneReadOnlyParts';

const THEME_OPTIONS = [
  { value: 'system', labelKey: 'appearance.themeOptions.system', icon: LuMonitor },
  { value: 'light', labelKey: 'appearance.themeOptions.light', icon: LuSun },
  { value: 'dark', labelKey: 'appearance.themeOptions.dark', icon: LuMoon },
];

/** A 52px row: an icon, a label with an optional line under it, and a trailing control. */
function Row({
  icon, label, sub, trailing, color, onClick, onRowTap, isDisabled = false,
}) {
  const tappable = Boolean(onClick || onRowTap) && !isDisabled;
  return (
    <Flex
      as={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={tappable ? (onClick || onRowTap) : undefined}
      disabled={onClick ? isDisabled : undefined}
      align="center"
      gap="12px"
      w="100%"
      minH="52px"
      px="14px"
      py="8px"
      textAlign="left"
      cursor={tappable ? 'pointer' : 'default'}
      opacity={isDisabled ? 0.5 : 1}
      color={color || 'redesign.heading'}
      _active={tappable ? { bg: 'redesign.bg.hover' } : undefined}
    >
      {icon && <Box as={icon} boxSize="20px" flexShrink={0} color={color || 'phone.muted'} />}
      <Box flex="1" minW={0}>
        <Text textStyle="phoneBody">{label}</Text>
        {sub && <Text textStyle="phoneMeta" color="phone.muted">{sub}</Text>}
      </Box>
      {trailing}
    </Flex>
  );
}

/**
 * One choice among a few, as iOS settings lists draw it: a 52px row per
 * option, the chosen one ticked.
 */
function ChoiceRows({ label, options, value, onChange }) {
  const inset = options.some((o) => o.icon) ? '46px' : '14px';
  return (
    <ListCard mt="0">
      {/* The group wraps the rows, so it draws their hairlines itself. */}
      <Box
        role="radiogroup"
        aria-label={label}
        sx={{
          '& > * + *': { position: 'relative' },
          '& > * + *::before': {
            content: '""', position: 'absolute', top: 0, left: inset, right: 0,
            height: '1px', bg: 'redesign.border',
          },
        }}
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <Flex
              key={opt.value}
              as="button"
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.value)}
              align="center"
              gap="12px"
              w="100%"
              minH="52px"
              px="14px"
              textAlign="left"
              color="redesign.heading"
              _active={{ bg: 'redesign.bg.hover' }}
            >
              {opt.icon && <Box as={opt.icon} boxSize="20px" flexShrink={0} color="phone.muted" />}
              <Text textStyle="phoneBody" flex="1" minW={0}>{opt.label}</Text>
              {active && <Box as={LuCheck} boxSize="20px" flexShrink={0} color="phone.link" />}
            </Flex>
          );
        })}
      </Box>
    </ListCard>
  );
}

function SettingsBody({ onLogout }) {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { user } = useAuth();
  const { preference, setPreference } = useColorPreference();
  const { setLanguage, allAlertsEnabled, setAllAlerts } = usePreferences();
  const currentLng = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

  return (
    <Box pb="8px">
      <SectionCaption pt="4px">{t('sections.account')}</SectionCaption>
      <ListCard mt="0" dividerInset="46px">
        <Flex align="center" gap="12px" minH="64px" px="14px" py="10px">
          <Avatar
            src={user?.picture}
            name={user?.full_name || user?.username}
            size="sm"
            bg="redesign.bg.hover"
            color="redesign.text"
          />
          <Box minW={0}>
            <Text textStyle="phoneRowName" color="redesign.heading" isTruncated>
              {user?.full_name || user?.username || '—'}
            </Text>
            <Text textStyle="phoneMeta" color="phone.muted" isTruncated>{user?.email || '—'}</Text>
          </Box>
        </Flex>
        <Row icon={LuLogOut} label={t('common:actions.logout')} color="phone.expense" onClick={onLogout} />
      </ListCard>

      <SectionCaption>{t('sections.appearance')}</SectionCaption>
      <ChoiceRows
        label={t('appearance.themeAriaLabel')}
        value={preference}
        onChange={setPreference}
        options={THEME_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey), icon: o.icon }))}
      />

      <SectionCaption>{t('sections.language')}</SectionCaption>
      <ChoiceRows
        label={t('language.ariaLabel')}
        value={currentLng}
        onChange={setLanguage}
        options={SUPPORTED_LANGUAGES.map((l) => ({ value: l.code, label: l.nativeLabel }))}
      />

      <SectionCaption>{t('sections.alerts')}</SectionCaption>
      <ListCard mt="0">
        {/* The whole row is the target. Chakra's Switch is itself a <label>,
            so the row cannot be one too: the row toggles on its own tap, and
            the switch lets taps through to it, keeping only the keyboard. */}
        <Row
          label={t('alerts.all.label')}
          sub={t('phone.alertsNote')}
          onRowTap={() => setAllAlerts(!allAlertsEnabled)}
          trailing={(
            <Switch
              isChecked={allAlertsEnabled}
              onChange={(e) => setAllAlerts(e.target.checked)}
              aria-label={t('alerts.all.label')}
              colorScheme="blue"
              size="lg"
              pointerEvents="none"
            />
          )}
        />
      </ListCard>

      <Text textStyle="phoneMeta" color="phone.muted" px="32px" pt="16px">
        {t('phone.footer')}
      </Text>
    </Box>
  );
}

/**
 * Settings on the phone: one full-height sheet, a vertical list instead of
 * the desktop's sections. Account (with Log out behind a question),
 * Appearance, Language and the master alerts switch.
 * Currencies, Features, MCP and Members are not listed: they are managed on
 * a computer.
 */
export default function PhoneSettingsList({ isOpen, onClose }) {
  const { t } = useTranslation(['settings', 'navigation', 'common']);
  const [logoutOpen, setLogoutOpen] = useState(false);

  return (
    <PhoneSheet id="settings" isOpen={isOpen} onClose={onClose} size="full" title={t('settings:title')}>
      <SettingsBody onLogout={() => setLogoutOpen(true)} />
      <LogoutConfirm isOpen={logoutOpen} onCancel={() => setLogoutOpen(false)} />
    </PhoneSheet>
  );
}

/** Log out asks first on the phone, as it does from More. */
function LogoutConfirm({ isOpen, onCancel }) {
  const { t } = useTranslation(['navigation', 'common']);
  const { close } = usePhoneSheet();
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <PhoneConfirmSheet
      isOpen={isOpen}
      title={t('navigation:more.logoutTitle')}
      confirmLabel={t('common:actions.logout')}
      destructive
      onConfirm={async () => {
        onCancel();
        await close();
        logout();
        navigate('/login');
      }}
      onCancel={onCancel}
    />
  );
}
